import { skipToken } from "@reduxjs/toolkit/query";
import { createContext, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  useCreateMessageMutation,
  useGetMessagesQuery,
} from "src/api/services/messagesApi";
import { MessageType } from "src/api/types";
import { MatchParam } from "src/constants/matches";
import { usePatient } from "../PatientProvider";

interface ContextType {
  messages: MessageType[];
  sendMessage: (message: string) => void;
}

export const MessagingContext = createContext<ContextType>({
  messages: [],
  sendMessage: () => {},
});

interface Props {
  children: React.ReactNode;
}

const MessagingProvider = ({ children }: Props) => {
  const language = usePatient((p) => p.profile.language);
  const { threadId } = useParams<MatchParam<"THREAD_ID">>();
  const { data } = useGetMessagesQuery(threadId ?? skipToken);
  const [createMessage] = useCreateMessageMutation();

  const sendMessage = useCallback(
    (message: string) => {
      if (threadId == null) return;

      createMessage({
        thread_slug: threadId,
        message,
        language,
      });
    },
    [createMessage, language, threadId],
  );

  const messages = useMemo(() => {
    if (data == null) return [];
    return data;
  }, [data]);

  const value = useMemo(
    () => ({
      messages,
      sendMessage,
    }),
    [messages, sendMessage],
  );

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

export default MessagingProvider;
